import type { DealerContextProps, Locale, Permissions } from "@porsche-customer/slfinrtl-context-types"
import { currentEnvironment } from "@porsche-customer/slfinrtl-environment"
import { useCognito } from "@slfinrtl/cognito-react"
import type { RequestType } from "@slfinrtl/cognito-react"
import { getDealerContextRoute } from "@slfinrtl/ui-bff-routes"
import { isAxiosError } from "axios"
import { useCallback, useMemo } from "react"
import { callBff } from "../../common/callBff"
import { getCognitoConfig } from "../../common/getCognitoConfig"

export interface FetchDealerContextError {
	message?: string
	redirectUrl?: string
}

export type DealerContextResult = Omit<DealerContextProps, "getReferralId"> & { permissions: Permissions }

export const useFetchDealerContext = (
	dealershipId: string,
	locale?: Locale,
	onError?: () => void,
): (() => Promise<DealerContextResult | undefined>) => {
	const cognitoConfig = useMemo(() => getCognitoConfig(), [])
	const environment = currentEnvironment()

	const onCognitoError = useCallback(
		(_type: RequestType, _error: unknown) => {
			onError?.()
		},
		[onError],
	)

	const { getAuthorizationHeader } = useCognito(cognitoConfig, onCognitoError)

	return useCallback(async () => {
		const contextView = await callBff(
			{
				dealershipId,
				environment,
				getAuthorizationHeader,
				locale,
			},
			getDealerContextRoute,
			{ body: undefined, pathParams: {}, queryParams: {} },
		).catch((error) => {
			if (!isAxiosError(error)) {
				return
			}
			// Redirect if we get an 404 with a redirectUri attached
			if (error.response?.status === 404 && error.response.data.redirectUri) {
				const redirectUrl = error.response.data.redirectUri
				// eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
				return Promise.reject({ redirectUrl })
			}
			// eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
			return Promise.reject({
				message: error.response?.data?.message ?? error.message,
			})
		})

		// Redirect if current locale is not valid in marketplace
		if (contextView?.availableLocales.length && (!locale || !contextView.availableLocales.includes(locale))) {
			const redirectPath = window.location.pathname.replace(
				/([^/]+)(\/[^/]+(.*)|.*)/,
				`$1/${contextView.availableLocales[0]}$3`,
			)
			const redirectUrl = `${window.location.origin}${redirectPath}`
			// eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
			return Promise.reject({ redirectUrl })
		}

		if (contextView) {
			return {
				...contextView,
				dealershipId: contextView.entryDn,
				environment,
				getAuthorizationHeader,
				locale: locale!,
			}
		}
	}, [dealershipId, environment, getAuthorizationHeader, locale])
}
